/**
 * Canonical attribution vocabulary and storage contract. Every parameter name,
 * status, limit, and cookie attribute used by this module comes from here —
 * there are no attribution string literals at call sites or inside `utm.js` /
 * `cookie.js` / `attribution.js`. Keeping the contract in one frozen place is
 * what lets the module be vendored byte-identical across agapae.gr,
 * app.agapae.gr, and invite.agapae.gr without the three copies drifting apart.
 *
 * READ THIS BEFORE CHANGING A VALUE. The cookie described here is a PENDING,
 * NON-AUTHORITATIVE staging record — see the note on
 * `ATTRIBUTION_COOKIE_NAME` — but it still lands on real devices, and its shape
 * is what a future `POST /api/attribution/touch` will drain. Changing a key
 * silently orphans every record already written. See
 * docs/analytics/attribution-foundation.md.
 */

/**
 * Name of the first-party cookie holding the PENDING attribution record.
 *
 * The `_pending` suffix is load-bearing, not decoration. The approved
 * architecture makes a server-set, `HttpOnly`, `.agapae.gr`-scoped cookie named
 * `agapae_attr` the AUTHORITATIVE store, written by the attribution endpoint in
 * a later phase. That cookie cannot exist yet (there is no endpoint), and this
 * one must never be mistaken for it:
 *
 *   - This cookie is written by page JavaScript, so it is readable and
 *     forgeable by anything running on the page. It can never be trusted as the
 *     basis of a revenue report.
 *   - Safari's ITP caps JS-written storage at ~7 days regardless of the
 *     `expires` we set below. That is precisely why the authoritative record is
 *     server-set — an HTTP-set cookie is not subject to the cap.
 *
 * So: this is a staging buffer that carries a campaign click across the gap
 * between "visitor arrived" and "a backend exists to tell". Distinct name,
 * distinct trust level, no collision when the real cookie ships.
 * @readonly
 */
export const ATTRIBUTION_COOKIE_NAME = "agapae_attr_pending";

/**
 * How long a pending attribution record survives, in days.
 *
 * 90 days matches GA4's default acquisition lookback and Meta's click window,
 * and is the same window the authoritative cookie will use — deliberately, so
 * the two can never disagree about when a first touch went stale. It is also
 * the retention limit for the raw click identifiers below, which are device
 * identifiers and therefore personal data.
 *
 * Sliding, but only on a real campaign touch — see `cookie.js`. A visitor who
 * returns fifty times by bookmark does not extend their own attribution window;
 * only a new inbound campaign click does.
 * @readonly
 */
export const ATTRIBUTION_COOKIE_MAX_AGE_DAYS = 90;

/** Cookie path — site-wide, so every route on a surface sees the same record. @readonly */
export const ATTRIBUTION_COOKIE_PATH = "/";

/**
 * `SameSite=Lax` — the record must survive the top-level navigation from an ad
 * or a search result (the only moment attribution is ever created) but never
 * ride a cross-site subrequest.
 * @readonly
 */
export const ATTRIBUTION_COOKIE_SAME_SITE = "Lax";

/**
 * The registrable domain the surfaces share. When the current hostname matches,
 * the cookie is scoped to `ATTRIBUTION_COOKIE_DOMAIN` so a campaign click that
 * lands on agapae.gr is still visible after the hop to app.agapae.gr — the hop
 * where attribution is destroyed today. On localhost (ports, not subdomains) no
 * domain attribute is set. Same test as the consent foundation.
 * @readonly
 */
export const ATTRIBUTION_COOKIE_DOMAIN_SUFFIX = "agapae.gr";

/** The `domain=` attribute value used on production hosts. @readonly */
export const ATTRIBUTION_COOKIE_DOMAIN = ".agapae.gr";

/**
 * Path of the endpoint that takes ownership of a staged record.
 *
 * Appended to the API base URL the entry point supplies. It is a SEAM with the
 * backend: the route is mounted at `/api/attribution` in api/index.mjs and
 * declares `/touch` in api/routes/attribution.routes.mjs. A rename on either
 * side produces a 404, which this module treats as a retryable failure — so the
 * staging record would survive but never be handed over, and attribution would
 * report zero with every test on both sides still green. `npm run verify:attribution`
 * asserts the two agree.
 * @readonly
 */
export const ATTRIBUTION_TOUCH_PATH = "/api/attribution/touch";

/**
 * Storage-format version, written as `v` on every record.
 *
 * Present from the first release so a future shape change can be detected
 * rather than guessed at. A record whose version this module does not recognize
 * is discarded, not coerced — a misread attribution record is worse than none,
 * because it produces a confident wrong campaign rather than an obvious gap.
 * @readonly
 */
export const ATTRIBUTION_SCHEMA_VERSION = 1;

/**
 * The five standard UTM parameters, mapped to the semantic field they populate.
 *
 * `utm_id` is deliberately absent: GA4 uses it for its own campaign-ID joins,
 * nothing here consumes it, and an unused stored field is a field that drifts.
 * @readonly
 */
export const UTM_PARAMETERS = Object.freeze({
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_content: "content",
  utm_term: "term",
});

/**
 * Advertising click identifiers, mapped to the semantic field they populate.
 *
 * These are the "capture now or lose it forever" values. `fbclid` in particular
 * cannot be reconstructed after the fact: if it is not recorded at the moment
 * of the click, Meta match quality for that visitor is permanently
 * unrecoverable. They are also device identifiers — personal data — which is
 * why they are consent-gated and expire with the record.
 * @readonly
 */
export const CLICK_ID_PARAMETERS = Object.freeze({
  gclid: "gclid",
  fbclid: "fbclid",
  msclkid: "msclkid",
});

/**
 * Attribution outcomes. Four distinct business realities that must never be
 * collapsed into "no attribution" — the moment they are, the direct bucket
 * silently absorbs everything and the campaign report stops being trustworthy.
 *
 *   CAPTURED       a campaign touch was recorded.
 *   DIRECT         the visitor arrived with no campaign signal at all. Real,
 *                  and different from a failure.
 *   CONSENT_DENIED a campaign touch existed and we were not permitted to keep
 *                  it. Sizing this bucket is how the unattributed share stays
 *                  explainable.
 *   UNAVAILABLE    no storage (non-browser, disabled cookies). A defect or a
 *                  hostile environment, not a visitor behaviour.
 * @readonly
 */
export const AttributionStatus = Object.freeze({
  CAPTURED: "captured",
  DIRECT: "direct",
  CONSENT_DENIED: "consent_denied",
  UNAVAILABLE: "unavailable",
});

/**
 * Outcomes of one handoff attempt (`sendPendingAttribution`).
 *
 * Returned for tests and for the caller's own logging; nothing in the UI reads
 * them. Eight distinct realities, because "the record was not handed over" has
 * eight causes and only one of them is a bug worth chasing:
 *
 *   SENT              the server acknowledged, and the staging record is gone.
 *   NOTHING_STAGED    no record to send. The ordinary case on a return visit —
 *                     a successful handoff erased the staging copy.
 *   CONSENT_DENIED    marketing consent is not granted. Never send.
 *   NOT_CONFIGURED    no API base URL was supplied, so the surface is capture-
 *                     only. Deliberate, not an error.
 *   CROSS_SITE        the endpoint is not same-site with this page, so cookies
 *                     could not ride the request. See `isSameSiteEndpoint`.
 *   ALREADY_ATTEMPTED one attempt per page load, already spent.
 *   FAILED            offline, a network error, or any non-204 answer. The
 *                     record is KEPT and retried on the next page load.
 *   UNAVAILABLE       no DOM or no `fetch` — SSR, a test runner, an ancient
 *                     browser. Nothing was attempted.
 * @readonly
 */
export const BeaconOutcome = Object.freeze({
  SENT: "sent",
  NOTHING_STAGED: "nothing_staged",
  CONSENT_DENIED: "consent_denied",
  NOT_CONFIGURED: "not_configured",
  CROSS_SITE: "cross_site",
  ALREADY_ATTEMPTED: "already_attempted",
  FAILED: "failed",
  UNAVAILABLE: "unavailable",
});

/**
 * The reported attribution model.
 *
 * FIRST_TOUCH is the answer this business needs: the purchase happens once, the
 * consideration cycle runs for months, and the only marketing question with
 * money attached is "what made this couple find us?". Last touch is stored
 * alongside it — cheap, and the only way to reconcile our numbers with Meta's
 * and Google's last-click reporting — but it is not what we report.
 *
 * Documented consequence, so nobody "fixes" it later: first-touch systematically
 * under-credits retargeting and over-credits top-of-funnel. For a
 * discovery-driven product that is the correct bias. It is a bias, not an error.
 * @readonly
 */
export const AttributionModel = Object.freeze({
  FIRST_TOUCH: "first_touch",
  LAST_TOUCH: "last_touch",
});

/**
 * Per-field length caps, applied at capture.
 *
 * The cookie rides every request to `.agapae.gr`, so unbounded values would tax
 * every page load and every API call. These are abuse ceilings, not expected
 * sizes — a real UTM is 10–40 characters.
 * @readonly
 */
export const FIELD_LIMITS = Object.freeze({
  utm: 100,
  clickId: 128,
  referrer: 128,
  landingPath: 128,
});

/**
 * Byte ceiling for the ENCODED cookie value, before percent-encoding.
 *
 * Browsers drop cookies over ~4KB, and a dropped cookie is a silent total loss.
 * When a record would exceed this, `cookie.js` sheds data in a fixed priority
 * order and records what it shed — see `DROPPED_MARKERS`. First touch is the
 * reported model, so it is the last thing to go.
 * @readonly
 */
export const MAX_ENCODED_COOKIE_BYTES = 1024;

/**
 * Markers written into the record's `d` array when the size guard sheds data.
 *
 * A cap without a flag is a lie: a consumer reading a record with no last touch
 * must be able to tell "there was only ever one touch" from "we could not fit
 * the second". `touchCount` alone cannot express that.
 * @readonly
 */
export const DROPPED_MARKERS = Object.freeze({
  LAST_TOUCH_DETAIL: "lt_detail",
  LAST_TOUCH: "lt",
  FIRST_TOUCH_DETAIL: "ft_detail",
});

/**
 * Compact keys for the stored record. The wire format is abbreviated because
 * this payload sits in a cookie header on every request; the semantic names
 * live in `types.js` and are what every consumer sees.
 *
 * These are the storage contract. Renaming one orphans every record already on
 * a device.
 * @readonly
 */
export const STORAGE_KEYS = Object.freeze({
  version: "v",
  firstTouch: "ft",
  lastTouch: "lt",
  touchCount: "n",
  dropped: "d",
  source: "s",
  medium: "m",
  campaign: "c",
  content: "ct",
  term: "tm",
  referrer: "r",
  landingPath: "lp",
  gclid: "g",
  fbclid: "f",
  msclkid: "ms",
  timestamp: "ts",
});
